import { useState, useRef } from "react";
import { useEditorStore } from "@/store/editorStore";
import ElementRenderer from "./ElementRenderer";
import FloatingPageToolbar from "./FloatingPageToolbar";
import ElementContextToolbar from "./ElementContextToolbar";
import { Palette } from "lucide-react";

const CANVAS_WIDTH = 1024;
const CANVAS_HEIGHT = 768;

export default function CanvasStack() {
  const pages = useEditorStore((s) => s.pages);
  const activePageId = useEditorStore((s) => s.activePageId);
  const setActivePage = useEditorStore((s) => s.setActivePage);
  const setSelectedElement = useEditorStore((s) => s.setSelectedElement);
  const zoom = useEditorStore((s) => s.zoom);

  return (
    <div
      className="ch-canvas-area flex-1 overflow-y-auto ch-scroll p-12 flex flex-col items-center gap-16 bg-[#E5E7EB]"
      data-testid="canvas-stack"
      onClick={() => setSelectedElement(null)}
    >
      <div style={{ transform: `scale(${zoom})`, transformOrigin: "top center" }} className="flex flex-col items-center gap-16">
        {pages.map((page, idx) => (
          <PageBlock
            key={page.id}
            page={page}
            index={idx}
            total={pages.length}
            focused={activePageId === page.id}
            onFocus={() => setActivePage(page.id)}
          />
        ))}
      </div>
    </div>
  );
}

function PageBlock({ page, index, total, focused, onFocus }) {
  const addElement = useEditorStore((s) => s.addElement);
  const setPageBackgroundImage = useEditorStore((s) => s.setPageBackgroundImage);
  const setPageBackground = useEditorStore((s) => s.setPageBackground);
  const selectedElementId = useEditorStore((s) => s.selectedElementId);
  const updateElement = useEditorStore((s) => s.updateElement);
  const zoom = useEditorStore((s) => s.zoom);

  const [pendingDrop, setPendingDrop] = useState(null); // { url, x, y }
  const [showColorPicker, setShowColorPicker] = useState(false);
  const artboardRef = useRef(null);
  const [dropActive, setDropActive] = useState(false);

  const onDragOver = (e) => {
    if (e.dataTransfer.types.includes("application/ch-image")) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
      setDropActive(true);
    }
  };

  const onDragLeave = () => setDropActive(false);

  const onDrop = (e) => {
    setDropActive(false);
    const raw = e.dataTransfer.getData("application/ch-image");
    if (!raw) return;
    e.preventDefault();
    const data = JSON.parse(raw);
    const rect = artboardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;

    // Check if dropped on existing element — replace its source
    const targetEl = e.target.closest("[data-element-id]");
    if (targetEl) {
      const elementId = targetEl.dataset.elementId;
      const element = page.elements.find((el) => el.id === elementId);
      if (element && element.type === "image") {
        updateElement(page.id, elementId, { src: data.url });
        return;
      }
    }
    // Otherwise, ask user: standalone vs background
    setPendingDrop({ url: data.url, x: x - 100, y: y - 75 });
  };

  return (
    <div className="relative ch-fade" data-testid={`page-block-${page.id}`}>
      <div className="absolute -left-12 top-2 text-[10px] font-bold uppercase tracking-wider text-gray-400 font-mono">
        {index + 1} / {total}
      </div>
      <div
        ref={artboardRef}
        className={`ch-artboard ${focused ? "focused" : ""} ${dropActive ? "ch-drop-active" : ""}`}
        style={{
          width: CANVAS_WIDTH,
          height: CANVAS_HEIGHT,
          background: page.backgroundImage
            ? `url(${page.backgroundImage}) center/cover no-repeat`
            : page.background || "#FFFFFF",
        }}
        onClick={(e) => {
          e.stopPropagation();
          onFocus();
        }}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        data-testid={`artboard-${page.id}`}
      >
        {page.elements.map((el) => (
          <ElementRenderer key={el.id} element={el} pageId={page.id} scale={zoom} />
        ))}

        {/* Color picker docker */}
        {focused && (
          <button
            className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-gray-200 shadow text-xs font-semibold hover:bg-gray-50"
            onClick={(e) => {
              e.stopPropagation();
              setShowColorPicker((s) => !s);
            }}
            data-testid={`page-color-button-${page.id}`}
          >
            <Palette className="w-4 h-4 text-[#002FA7]" />
            Page color
          </button>
        )}
        {showColorPicker && (
          <ColorDocker
            current={page.background}
            onPick={(c) => {
              setPageBackground(page.id, c);
              setShowColorPicker(false);
            }}
            onClose={() => setShowColorPicker(false)}
          />
        )}

        {/* Element context toolbar */}
        {focused &&
          selectedElementId &&
          page.elements.some((e) => e.id === selectedElementId) && (
            <ElementContextToolbar pageId={page.id} elementId={selectedElementId} />
          )}
      </div>

      {/* Floating page toolbar — right side */}
      <FloatingPageToolbar pageId={page.id} index={index} total={total} />

      {pendingDrop && (
        <ImageDropChoice
          onChoose={(mode) => {
            if (mode === "background") {
              setPageBackgroundImage(page.id, pendingDrop.url);
            } else {
              addElement(page.id, {
                type: "image",
                x: pendingDrop.x,
                y: pendingDrop.y,
                w: 320,
                h: 200,
                src: pendingDrop.url,
                fit: "cover",
              });
            }
            setPendingDrop(null);
          }}
          onCancel={() => setPendingDrop(null)}
        />
      )}
    </div>
  );
}

function ColorDocker({ current, onPick, onClose }) {
  const PALETTE = [
    "#FFFFFF", "#F9FAFB", "#E5E7EB", "#9CA3AF", "#111827",
    "#002FA7", "#1D4ED8", "#3B82F6", "#10B981", "#FACC15",
    "#FF8C00", "#FF4500", "#EF4444", "#EC4899", "#8B5CF6",
  ];
  const [hex, setHex] = useState(current || "#FFFFFF");
  return (
    <div
      className="absolute bottom-14 left-3 w-64 bg-white border border-gray-200 rounded-md shadow-xl p-3 z-30"
      onClick={(e) => e.stopPropagation()}
      data-testid="color-docker"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Page color</span>
        <button onClick={onClose} className="text-xs text-gray-400 hover:text-gray-700">Close</button>
      </div>
      <div className="grid grid-cols-5 gap-2 mb-3">
        {PALETTE.map((c) => (
          <button
            key={c}
            className="aspect-square rounded ring-1 ring-gray-200 hover:ring-2 hover:ring-[#002FA7]"
            style={{ background: c }}
            onClick={() => onPick(c)}
            data-testid={`color-swatch-${c.replace("#", "")}`}
            title={c}
          />
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={hex}
          onChange={(e) => setHex(e.target.value)}
          className="w-9 h-9 rounded border border-gray-200"
          data-testid="color-input"
        />
        <input
          value={hex}
          onChange={(e) => setHex(e.target.value)}
          className="flex-1 px-2 py-1 border border-gray-200 rounded text-xs font-mono"
        />
        <button
          className="bg-[#002FA7] text-white text-xs px-3 py-1.5 rounded"
          onClick={() => onPick(hex)}
          data-testid="color-apply"
        >
          Apply
        </button>
      </div>
    </div>
  );
}

function ImageDropChoice({ onChoose, onCancel }) {
  return (
    <div
      className="absolute inset-0 flex items-center justify-center bg-black/30 z-30 rounded"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-lg shadow-xl p-5 max-w-sm space-y-3"
        onClick={(e) => e.stopPropagation()}
        data-testid="image-drop-choice"
      >
        <h3 className="font-display font-semibold tracking-tight">Place image as…</h3>
        <p className="text-xs text-gray-500">Choose how to insert this image on the page.</p>
        <div className="grid grid-cols-2 gap-2">
          <button
            className="border border-gray-200 rounded-md p-3 text-sm hover:bg-gray-50 hover:border-[#002FA7]"
            onClick={() => onChoose("element")}
            data-testid="drop-choice-element"
          >
            Standalone image
          </button>
          <button
            className="border border-gray-200 rounded-md p-3 text-sm hover:bg-gray-50 hover:border-[#002FA7]"
            onClick={() => onChoose("background")}
            data-testid="drop-choice-background"
          >
            Full page background
          </button>
        </div>
      </div>
    </div>
  );
}

// end of CanvasStack module

